import { useMemo } from "react";
import { DesignPreviewModal } from "./externPageGraffiti/GraffitiPreview";
import DesignStudio, {

  makeSlotDesign,
   useGraphicOrderSave,
   CSS_
} from "./externPageGraffiti/DesignStudio";
import { elakayAutoText } from "../dataElakay";
import { toImageUrl, getElakayImageLoadUrl } from "../../../config";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["graffitiPreview"]?.[key] || fallback;

const getHardcodedText = (infoSource, group, key, fallback) =>
  infoSource?.laguangeElakay?.("frontElakayHardcodedText", infoSource.languageAPI)?.[group]?.[key] || fallback;

/* ══════════════════════════════════════════════════════
   GraphicPreviewModal
   
   Router de preview selon sourceType :
   - "custom"   → DesignPreviewModal (slots customisés par l'acheteur)
   - "ask"      → AskSellerPreview (formulaire de demande)
   - "template" → DesignPreviewModal (template du vendeur)
══════════════════════════════════════════════════════ */
export default function GraphicPreviewModal({
  graphicInfo,
  product,
  infoSource,
  onClose,
  onEdit
}) {
  __setElakayInfoSource(infoSource);
  __setElakayInfoSource(infoSource);

  const sourceType = graphicInfo?.sourceType;

  const designForPreview = useMemo(() => {

    if (!graphicInfo) return null;

    const isDesignPreview =

      sourceType === "custom" ||

      sourceType === "template";

    if (!isDesignPreview) return null;

    const { slots = [], templateMeta, templateRef } = graphicInfo;

    return {

      _id: templateRef || `local_${graphicInfo?.submittedAt || Date.now()}`,

      label:

        templateMeta?.label ||

        (sourceType === "custom"

          ? "Your Custom Design"

          : "Selected Template"),

      style: templateMeta?.style || "—",

      detail: templateMeta?.detail || "—",

      slots: slots.map((s) => ({

        slotId: s.slotId,

        label: s.label,

        url: s.url || "",

        slotState: s.slotState

      })),

      designs: slots.map((s) => ({

        slotId: s.slotId,

        texts: s.slotState?.texts || [],

        layers: s.slotState?.layers || [],

        instructions: s.slotState?.instructions || ""

      })),

      createdAt: graphicInfo?.submittedAt,

      author: {

        name: sourceType === "custom" ? "You" : "Seller"

      }

    };

  }, [graphicInfo, sourceType]);

  if (!graphicInfo) return null;

  /* ═══════════════════════════════════════════════════
     MODE "custom" — DesignPreviewModal avec slots de l'acheteur
  ═══════════════════════════════════════════════════ */
  if (sourceType === "custom") {
    return (
      <>  <style>{CSS_}</style>
      <DesignPreviewModal
        design={designForPreview}
        onClose={onClose}
        onEdit={onEdit}
        showEditButton={true}
        /* Customisation du header */
        titleOverride="🎨 Your Custom Design"
        subtitleOverride={`Submitted ${formatDate(graphicInfo.submittedAt)}`}
        /* Footer : indique que c'est en attente du checkout */
        footerNote={
          <span style={{ color: "#5a7a8a", fontSize: 11 }}>{__elakayText("t021", "💡 Click \"Buy Now\" on the checkout page to finalize your order.")}</span>
        }
      />
      </>
    );
  }

  /* ═══════════════════════════════════════════════════
     MODE "template" — DesignPreviewModal avec le template choisi
     (et les éventuelles customisations de l'acheteur par-dessus)
  ═══════════════════════════════════════════════════ */
  if (sourceType === "template") {
    const hasCustomizations = graphicInfo.slots?.length > 0;

    return (
       <>  <style>{CSS_}</style>
      <DesignPreviewModal
        design={designForPreview}
        onClose={onClose}
        onEdit={onEdit}
        showEditButton={true}
        titleOverride={`📋 ${graphicInfo.templateMeta?.label || "Template"}`}
        subtitleOverride={
          hasCustomizations
            ? `Template by seller · Customized by you`
            : `Template by seller · Used as-is`
        }
        footerNote={
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ color: "#007399", fontSize: 11, fontWeight: 600 }}>{__elakayText("t022", "📋 Template ID:")}{graphicInfo.templateRef?.slice(-12) || "—"}
            </span>
            {hasCustomizations && (
              <span style={{ color: "#34c759", fontSize: 11, fontWeight: 600 }}>
                ✏️ {graphicInfo.slots.length}{__elakayText("t023", "face(s) customized")}</span>
            )}
            <span style={{ color: "#5a7a8a", fontSize: 11 }}>{__elakayText("t024", "💡 Click \"Buy Now\" to finalize your order.")}</span>
          </div>
        }
      />  
      </>
    );
  }

  /* ═══════════════════════════════════════════════════
     MODE "ask" — Vue custom dédiée au formulaire
  ═══════════════════════════════════════════════════ */
  if (sourceType === "ask") {
    return (
      <AskSellerPreview
        graphicInfo={graphicInfo}
        product={product}
        infoSource={infoSource}
        onClose={onClose}
        onEdit={onEdit}
      />
    );
  }

  return null;
}

/* ══════════════════════════════════════════════════════
   AskSellerPreview — Vue spécifique au mode "ask the seller"
══════════════════════════════════════════════════════ */
function AskSellerPreview({ graphicInfo, product, infoSource, onClose, onEdit }) {
  const text = (key, fallback) => getHardcodedText(infoSource, "graffitiPreview", key, fallback);

  const {
    askSellerData = {},
    variant,
    qty,
    totalPrice,
    submittedAt
  } = graphicInfo;

  const {
    specialInstructions,
    artistMessage,
    avoidInstructions,
    requiredInstructions,
    desiredDate,
    detailLevel,
    stylePrefs = [],
    inspirations = [],
    budget,
    refs = []
  } = askSellerData;

  const styles = {
    overlay: {
      position: "fixed", inset: 0,
      background: "rgba(13,45,61,.65)",
      backdropFilter: "blur(8px)",
      zIndex: 2100,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
      animation: "gpm-fade-in .22s ease"
    },
    modal: {
      background: "#fff",
      border: "1px solid #daeaf5",
      borderRadius: 14,
      maxWidth: 760,
      width: "100%",
      maxHeight: "92vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxShadow: "0 32px 80px rgba(0,40,60,.25)",
      animation: "gpm-slide-up .28s cubic-bezier(.22,1,.36,1)"
    },
    header: {
      padding: "16px 22px",
      borderBottom: "1.5px solid #daeaf5",
      background: "linear-gradient(135deg, rgba(79,172,254,.06), transparent)",
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexShrink: 0
    },
    headerIcon: {
      width: 38, height: 38,
      borderRadius: 10,
      background: "rgba(79,172,254,.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 18,
      flexShrink: 0
    },
    headerInfo: { flex: 1, minWidth: 0 },
    title: {
      fontSize: 15,
      fontWeight: 700,
      color: "#0d2d3d",
      fontFamily: "'DM Sans', sans-serif",
      letterSpacing: ".2px"
    },
    subtitle: {
      fontSize: 11,
      color: "#5a7a8a",
      marginTop: 2
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "4px 10px",
      borderRadius: 12,
      fontSize: 10.5,
      fontWeight: 700,
      background: "rgba(79,172,254,.12)",
      color: "#4facfe",
      textTransform: "uppercase",
      letterSpacing: ".5px"
    },
    closeBtn: {
      width: 32, height: 32,
      borderRadius: "50%",
      border: "1px solid #daeaf5",
      background: "transparent",
      cursor: "pointer",
      fontSize: 14,
      color: "#5a7a8a",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all .18s ease",
      flexShrink: 0
    },
    body: {
      padding: 22,
      overflowY: "auto",
      flex: 1,
      background: "#f8fbfd"
    },
    productSummary: {
      display: "flex",
      gap: 12,
      padding: 12,
      background: "#fff",
      border: "1px solid #daeaf5",
      borderRadius: 10,
      marginBottom: 18,
      alignItems: "center"
    },
    productImg: {
      width: 56, height: 56,
      borderRadius: 8,
      objectFit: "cover",
      background: "#eef6fb",
      flexShrink: 0,
      border: "1px solid #daeaf5"
    },
    productInfo: { flex: 1, minWidth: 0 },
    productName: {
      fontSize: 13,
      fontWeight: 700,
      color: "#0d2d3d",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    },
    productMeta: {
      fontSize: 11,
      color: "#5a7a8a",
      marginTop: 2
    },
    productPrice: {
      fontSize: 18,
      fontWeight: 700,
      color: "#007399",
      fontFamily: "'DM Sans', sans-serif",
      flexShrink: 0
    },
    section: {
      background: "#fff",
      border: "1px solid #daeaf5",
      borderRadius: 12,
      marginBottom: 14,
      overflow: "hidden"
    },
    sectionHead: {
      padding: "11px 16px",
      borderBottom: "1px solid #eef6fb",
      display: "flex",
      alignItems: "center",
      gap: 8,
      background: "linear-gradient(180deg, rgba(0,115,153,.03), transparent)"
    },
    sectionIcon: {
      fontSize: 14,
      flexShrink: 0
    },
    sectionTitle: {
      fontSize: 11,
      fontWeight: 700,
      color: "#007399",
      textTransform: "uppercase",
      letterSpacing: ".7px",
      flex: 1
    },
    sectionBody: {
      padding: 14
    },
    kvRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      padding: "7px 0",
      borderBottom: "1px solid #eef6fb",
      fontSize: 12,
      gap: 12
    },
    kvRowLast: {
      borderBottom: "none"
    },
    key: {
      color: "#5a7a8a",
      fontWeight: 500,
      flexShrink: 0,
      minWidth: 90
    },
    value: {
      color: "#0d2d3d",
      fontWeight: 600,
      textAlign: "right",
      wordBreak: "break-word",
      flex: 1
    },
    valueHighlight: {
      color: "#007399",
      fontSize: 14
    },
    chipGroup: {
      display: "flex",
      flexWrap: "wrap",
      gap: 5
    },
    chip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: 14,
      fontSize: 10.5,
      fontWeight: 600,
      background: "#eef6fb",
      color: "#007399",
      border: "1px solid rgba(0,115,153,.15)"
    },
    chipStyle: {
      background: "rgba(79,172,254,.12)",
      color: "#4facfe",
      border: "1px solid rgba(79,172,254,.22)"
    },
    chipInspiration: {
      background: "rgba(52,199,89,.10)",
      color: "#34c759",
      border: "1px solid rgba(52,199,89,.22)"
    },
    instructionBox: {
      padding: "10px 12px",
      background: "rgba(0,115,153,.04)",
      borderLeft: "3px solid #007399",
      borderRadius: "0 8px 8px 0",
      fontSize: 12.5,
      color: "#0d2d3d",
      lineHeight: 1.6,
      whiteSpace: "pre-wrap"
    },
    instructionBoxAvoid: {
      background: "rgba(239,68,68,.04)",
      borderLeftColor: "#ef4444"
    },
    instructionBoxRequired: {
      background: "rgba(52,199,89,.05)",
      borderLeftColor: "#34c759"
    },
    fieldLabel: {
      fontSize: 10,
      color: "#5a7a8a",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: ".6px",
      marginBottom: 6
    },
    refGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
      gap: 8
    },
    refItem: {
      aspectRatio: "4/3",
      borderRadius: 8,
      overflow: "hidden",
      border: "1.5px solid #daeaf5",
      background: "#f4f8fc",
      position: "relative"
    },
    refImg: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block"
    },
    refLabel: {
      position: "absolute",
      bottom: 0, left: 0, right: 0,
      padding: "4px 8px",
      background: "linear-gradient(transparent, rgba(0,0,0,.7))",
      color: "#fff",
      fontSize: 9.5,
      fontWeight: 600,
      textAlign: "center"
    },
    emptyState: {
      textAlign: "center",
      padding: 16,
      color: "#8aa6b5",
      fontSize: 11.5,
      fontStyle: "italic"
    },
    timeline: {
      display: "flex",
      flexDirection: "column",
      gap: 10,
      padding: "4px 0"
    },
    timelineItem: {
      display: "flex",
      gap: 12,
      alignItems: "flex-start"
    },
    timelineDot: {
      width: 24, height: 24,
      borderRadius: "50%",
      background: "rgba(79,172,254,.15)",
      color: "#4facfe",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      flexShrink: 0,
      fontWeight: 700,
      border: "1.5px solid rgba(79,172,254,.3)"
    },
    timelineContent: {
      flex: 1,
      fontSize: 11.5,
      color: "#0d2d3d",
      lineHeight: 1.5
    },
    timelineTime: {
      fontSize: 10,
      color: "#8aa6b5",
      fontWeight: 600,
      marginTop: 2
    },
    footer: {
      padding: "14px 22px",
      borderTop: "1.5px solid #daeaf5",
      background: "#f4f8fc",
      display: "flex",
      justifyContent: "space-between",
      gap: 10,
      flexWrap: "wrap",
      flexShrink: 0
    },
    footerInfo: {
      fontSize: 11,
      color: "#5a7a8a",
      lineHeight: 1.5,
      flex: 1,
      minWidth: 200
    }
  };

  const refsCount = refs.filter(Boolean).length;
  const hasAnyInstructions = specialInstructions || artistMessage || avoidInstructions || requiredInstructions;

  return (
    <div style={styles.overlay} onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div style={styles.modal}>

        {/* HEADER */}
        <div style={styles.header}>
          <div style={styles.headerIcon}>💬</div>
          <div style={styles.headerInfo}>
            <div style={styles.title}>{text("t001", "Custom Request to Seller")}</div>
            <div style={styles.subtitle}>{__elakayText("t093", "Submitted")}{formatDate(submittedAt)}
            </div>
          </div>
          <span style={styles.badge}>{text("t002", "Ask Seller")}</span>
          <button type="button" style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* BODY */}
        <div style={styles.body}>

          {/* Product summary */}
          <div style={styles.productSummary}>
            {product?.urlImg && (
              <img
                src={getElakayImageLoadUrl(toImageUrl(product.urlImg), { width: 300 })}
                alt={product.name}
                style={styles.productImg}
                loading="lazy"
                decoding="async"
              />
            )}
            <div style={styles.productInfo}>
              <div style={styles.productName}>{product?.name || elakayAutoText("h_dd3b86d1ef21b9", "Product")}</div>
              <div style={styles.productMeta}>{__elakayText("t025", "Variant:")}<strong>{variant}</strong> · {text("t003", "Qty:")} <strong>{qty}</strong>
              </div>
            </div>
            <div style={styles.productPrice}>${totalPrice}</div>
          </div>

          {/* Section : Request details */}
          <div style={styles.section}>
            <div style={styles.sectionHead}>
              <span style={styles.sectionIcon}>📋</span>
              <span style={styles.sectionTitle}>{text("t004", "Request Details")}</span>
            </div>
            <div style={styles.sectionBody}>

              <div style={styles.kvRow}>
                <span style={styles.key}>{text("t005", "Detail level")}</span>
                <span style={styles.value}>{detailLevel || "—"}</span>
              </div>

              <div style={styles.kvRow}>
                <span style={styles.key}>{text("t006", "Budget")}</span>
                <span style={{ ...styles.value, ...styles.valueHighlight }}>
                  {budget || "—"}
                </span>
              </div>

              <div style={{ ...styles.kvRow, ...styles.kvRowLast }}>
                <span style={styles.key}>{text("t007", "Desired date")}</span>
                <span style={styles.value}>
                  {desiredDate ? formatDateOnly(desiredDate) : elakayAutoText("h_8bb749a035fbbe", "Flexible")}
                </span>
              </div>

            </div>
          </div>

          {/* Section : Style preferences + Inspirations */}
          {(stylePrefs.length > 0 || inspirations.length > 0) && (
            <div style={styles.section}>
              <div style={styles.sectionHead}>
                <span style={styles.sectionIcon}>🎭</span>
                <span style={styles.sectionTitle}>{text("t008", "Style & Inspiration")}</span>
              </div>
              <div style={styles.sectionBody}>

                {stylePrefs.length > 0 && (
                  <>
                    <div style={styles.fieldLabel}>{text("t009", "Artistic styles")}</div>
                    <div style={{ ...styles.chipGroup, marginBottom: 12 }}>
                      {stylePrefs.map((s, i) => (
                        <span key={i} style={{ ...styles.chip, ...styles.chipStyle }}>
                          {s}
                        </span>
                      ))}
                    </div>
                  </>
                )}

                {inspirations.length > 0 && (
                  <>
                    <div style={styles.fieldLabel}>{text("t010", "Inspirations")}</div>
                    <div style={styles.chipGroup}>
                      {inspirations.map((s, i) => (
                        <span key={i} style={{ ...styles.chip, ...styles.chipInspiration }}>
                          💡 {s}
                        </span>
                      ))}
                    </div>
                  </>
                )}

              </div>
            </div>
          )}

          {/* Section : Instructions */}
          {hasAnyInstructions && (
            <div style={styles.section}>
              <div style={styles.sectionHead}>
                <span style={styles.sectionIcon}>📝</span>
                <span style={styles.sectionTitle}>{text("t011", "Instructions")}</span>
              </div>
              <div style={styles.sectionBody}>

                {specialInstructions && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={styles.fieldLabel}>{text("t012", "General instructions")}</div>
                    <div style={styles.instructionBox}>
                      {specialInstructions}
                    </div>
                  </div>
                )}

                {artistMessage && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={styles.fieldLabel}>💌 {text("t013", "Message to the artist")}</div>
                    <div style={styles.instructionBox}>
                      {artistMessage}
                    </div>
                  </div>
                )}

                {requiredInstructions && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={styles.fieldLabel}>
                      <span style={{ color: "#34c759" }}>✅ {text("t014", "Must include")}</span>
                    </div>
                    <div style={{ ...styles.instructionBox, ...styles.instructionBoxRequired }}>
                      {requiredInstructions}
                    </div>
                  </div>
                )}

                {avoidInstructions && (
                  <div>
                    <div style={styles.fieldLabel}>
                      <span style={{ color: "#ef4444" }}>❌ {text("t015", "Things to avoid")}</span>
                    </div>
                    <div style={{ ...styles.instructionBox, ...styles.instructionBoxAvoid }}>
                      {avoidInstructions}
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Section : Reference images */}
          <div style={styles.section}>
            <div style={styles.sectionHead}>
              <span style={styles.sectionIcon}>🖼️</span>
              <span style={styles.sectionTitle}>{__elakayText("t026", "Reference Images (")}{refsCount}/4)
              </span>
            </div>
            <div style={styles.sectionBody}>
              {refsCount > 0 ? (
                <div style={styles.refGrid}>
                  {refs.map((ref, i) => {
                    if (!ref) return null;
                    const url = ref instanceof File 
                      ? URL.createObjectURL(ref) 
                      : (typeof ref === "string" ? ref : ref.url);
                    return (
                      <div key={i} style={styles.refItem}>
                        <img src={url} alt={elakayAutoText("h_5e13f5795099fc", "Reference {value0}", { value0: i+1 })} style={styles.refImg} />
                        <div style={styles.refLabel}>{__elakayText("t094", "Ref")}{i+1}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={styles.emptyState}>{__elakayText("t027", "No reference images attached")}</div>
              )}
            </div>
          </div>

          {/* Section : What happens next */}
          <div style={styles.section}>
            <div style={styles.sectionHead}>
              <span style={styles.sectionIcon}>📅</span>
              <span style={styles.sectionTitle}>{text("t016", "What happens next")}</span>
            </div>
            <div style={styles.sectionBody}>
              <div style={styles.timeline}>

                <div style={styles.timelineItem}>
                  <div style={styles.timelineDot}>1</div>
                  <div>
                    <div style={styles.timelineContent}>
                      <strong>{text("t017", "You finalize this order.")}</strong>
                      <br/>{__elakayText("t028", "Click \"Buy Now\" on the checkout page to confirm.")}</div>
                  </div>
                </div>

                <div style={styles.timelineItem}>
                  <div style={styles.timelineDot}>2</div>
                  <div>
                    <div style={styles.timelineContent}>
                      <strong>{text("t018", "The seller reviews your request.")}</strong>
                      <br/>{__elakayText("t029", "You'll be notified within 24-48 hours.")}</div>
                  </div>
                </div>

                <div style={styles.timelineItem}>
                  <div style={styles.timelineDot}>3</div>
                  <div>
                    <div style={styles.timelineContent}>
                      <strong>{text("t019", "The seller sends you a draft.")}</strong>
                      <br/>{__elakayText("t030", "You can request changes or approve it.")}</div>
                  </div>
                </div>

                <div style={styles.timelineItem}>
                  <div style={styles.timelineDot}>4</div>
                  <div>
                    <div style={styles.timelineContent}>
                      <strong>{text("t020", "The product is manufactured & shipped.")}</strong>
                      <br/>{__elakayText("t031", "Once approved, the seller starts production.")}</div>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>

        {/* FOOTER */}
        <div style={styles.footer}>
          <div style={styles.footerInfo}>{__elakayText("t032", "💡 Your request has been saved. Click \"Buy Now\" on the checkout page to finalize your order.")}</div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button
              type="button"
              className="ck-btn ck-btn--ghost"
              onClick={onEdit}>{__elakayText("t033", "← Edit request")}</button>
            <button
              type="button"
              className="ck-btn ck-btn--primary"
              onClick={onClose}>{__elakayText("t034", "✓ Looks good")}</button>
          </div>
        </div>

      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════════ */
function formatDate(isoString) {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;

    return d.toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  } catch {
    return "—";
  }
}

function formatDateOnly(dateString) {
  if (!dateString) return "—";
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      day: "numeric", month: "long", year: "numeric"
    });
  } catch {
    return dateString;
  }
}
